<template>
  <div class="pickup-dialog-wrapper">
    <!-- 主弹窗：门店列表 + 确认按钮 -->
    <el-dialog
      :visible.sync="dialogVisible"
      title="确认线下领取"
      width="90%"
      :show-close="true"
      :close-on-click-modal="false"
      custom-class="pickup-dialog"
      @close="handleClose"
    >
      <div class="pickup-content">
        <!-- 订单信息摘要 -->
        <div class="order-summary" v-if="orderInfo">
          <div class="summary-item" v-if="orderInfo.goods_name">
            <span class="label">商品名称：</span>
            <span class="value">{{ orderInfo.goods_name }}</span>
          </div>
          <div class="summary-item" v-if="orderInfo.order_no">
            <span class="label">订单号：</span>
            <span class="value order-no">{{ orderInfo.order_no }}</span>
          </div>
        </div>

        <!-- 领取地点标题栏 -->
        <div class="store-section-header">
          <h4 class="section-title">
            <span class="title-icon">📍</span> 领取地点
            <span class="store-count">共 {{ storeList.length }} 家门店</span>
          </h4>
          <button
            class="btn-toggle-expand"
            @click="toggleExpand"
            v-if="storeList.length > 3"
          >
            {{ isExpanded ? '收起' : '展开' }}
            <i :class="['el-icon-arrow-' + (isExpanded ? 'up' : 'down')]"></i>
          </button>
        </div>

        <!-- 门店列表区域 -->
        <div :class="['store-list-wrapper', { 'is-expanded': isExpanded }]">
          <div
            v-for="(store, index) in displayStores"
            :key="store.id"
            class="store-card"
          >
            <div class="store-index">{{ index + 1 }}</div>
            <div class="store-info">
              <p class="store-name">{{ store.store_name }}</p>
              <p class="store-address">
                📍 {{ store.province }}{{ store.city }}{{ store.district }}{{ store.address }}
              </p>
              <p class="store-phone" v-if="store.contact_phone">
                📞 {{ store.contact_phone }}
              </p>
            </div>
          </div>

          <!-- 展开提示 -->
          <div class="expand-hint" v-if="!isExpanded && storeList.length > 3">
            <p>↓ 向下滑动查看更多门店 ↓</p>
          </div>
        </div>

        <p class="pickup-notice">
          <i class="el-icon-info"></i>
          确认线下领取后，将生成核销码，可前往任一门店核销
        </p>
      </div>

      <div slot="footer" class="pickup-footer">
        <button class="btn-pickup-cancel" @click="handleCancel">取消</button>
        <button
          class="btn-danger pickup-confirm-btn"
          @click="showConfirmPopup"
          :disabled="loading || storeList.length === 0"
        >
          {{ loading ? '提交中...' : '确认领取' }}
        </button>
      </div>
    </el-dialog>

    <!-- 二次确认弹窗 -->
    <el-dialog
      :visible.sync="confirmPopupVisible"
      title="⚠️ 二次确认"
      width="80%"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="confirm-popup-dialog"
      append-to-body
    >
      <div class="confirm-popup-content">
        <div class="popup-icon-warning">
          <i class="el-icon-warning-outline"></i>
        </div>
        <h3 class="popup-title">确定要选择线下领取吗？</h3>
        <p class="popup-desc">领取后将生成专属核销码，您需要到店出示核销码完成核销</p>
        <p class="popup-warning">
          <i class="el-icon-info"></i>
          此操作不可撤销，请谨慎操作
        </p>
      </div>
      <div slot="footer" class="popup-footer">
        <button class="btn-popup-cancel" @click="handleThinkAgain">再想想</button>
        <button
          class="btn-popup-confirm"
          @click="handleConfirm"
          :disabled="loading"
        >
          {{ loading ? '提交中...' : '确认线下领取' }}
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getStoreList } from '../api/store'
import { selectPickup } from '../api/order'

export default {
  name: 'ConfirmPickupDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      storeList: [],
      isExpanded: false,
      confirmPopupVisible: false,
      loading: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    orderId() {
      return this.orderInfo ? this.orderInfo.id || this.orderInfo.order_id : null
    },
    displayStores() {
      if (this.isExpanded) {
        return this.storeList
      }
      return this.storeList.slice(0, 3)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadStoreList()
        this.isExpanded = false
        this.confirmPopupVisible = false
      }
    }
  },
  methods: {
    async loadStoreList() {
      try {
        const res = await getStoreList()
        if (res.status === 1 && res.data && res.data.list) {
          this.storeList = res.data.list
        }
      } catch (error) {
        console.error('获取门店列表失败:', error)
        this.$message.error('获取门店列表失败')
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    handleCancel() {
      this.$emit('update:visible', false)
    },
    handleClose() {
      this.resetState()
    },
    showConfirmPopup() {
      this.confirmPopupVisible = true
    },
    handleThinkAgain() {
      this.confirmPopupVisible = false
      this.$emit('update:visible', false)
      this.resetState()
    },
    async handleConfirm() {
      if (this.loading) return

      this.loading = true
      try {
        const res = await selectPickup({ order_id: this.orderId })

        if (res.status === 1) {
          this.confirmPopupVisible = false
          this.$emit('confirm')
          this.resetState()
          this.$emit('update:visible', false)
        } else {
          this.$message.error(res.msg || '操作失败')
        }
      } catch (error) {
        console.error('确认领取失败:', error)
        this.$message.error(error.msg || error.message || '操作失败，请重试')
      } finally {
        this.loading = false
      }
    },
    resetState() {
      this.isExpanded = false
      this.confirmPopupVisible = false
      this.loading = false
    }
  }
}
</script>

<style scoped>
.pickup-dialog-wrapper {
  /* wrapper for nested dialogs */
}

.pickup-content {
  padding: 0 0 10px;
}

/* 订单信息摘要 */
.order-summary {
  background: #fafafa;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.summary-item {
  display: flex;
  margin-bottom: 8px;
  line-height: 1.5;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #909399;
  font-size: 13px;
  min-width: 72px;
  flex-shrink: 0;
}

.value {
  color: #303133;
  font-size: 13px;
  font-weight: 500;
  word-break: break-all;
}

.order-no {
  font-family: monospace;
  letter-spacing: 0.5px;
}

/* 领取地点标题栏 */
.store-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 2px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.title-icon {
  font-size: 16px;
}

.store-count {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
  margin-left: 6px;
}

.btn-toggle-expand {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 12px;
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #b3d8ff;
  border-radius: 14px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-toggle-expand:active {
  transform: scale(0.95);
  background: #d9ecff;
}

.btn-toggle-expand i {
  font-size: 11px;
}

/* 门店列表容器 */
.store-list-wrapper {
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  transition: max-height 0.3s ease;
}

.store-list-wrapper.is-expanded {
  max-height: 400px;
}

.store-list-wrapper::-webkit-scrollbar {
  width: 4px;
}

.store-list-wrapper::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 2px;
}

.store-list-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

/* 单个门店卡片 */
.store-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.2s;
}

.store-card:last-child {
  border-bottom: none;
}

.store-card:active {
  background: #f5f7fa;
}

.store-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.store-info {
  flex: 1;
  min-width: 0;
}

.store-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 6px 0;
}

.store-address {
  font-size: 12px;
  color: #606266;
  margin: 0 0 4px 0;
  line-height: 1.5;
  word-break: break-all;
}

.store-phone {
  font-size: 12px;
  color: #909399;
  margin: 0;
}

/* 展开提示 */
.expand-hint {
  text-align: center;
  padding: 10px 0;
}

.expand-hint p {
  font-size: 12px;
  color: #c0c4cc;
  margin: 0;
}

/* 提示文字 */
.pickup-notice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: #fdf6ec;
  border-radius: 8px;
  color: #e6a23c;
  font-size: 12px;
  margin-top: 14px;
  line-height: 1.5;
}

.pickup-notice i {
  font-size: 14px;
}

/* 底部按钮区 */
.pickup-footer {
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.btn-pickup-cancel, .pickup-confirm-btn {
  flex: 1;
  height: 46px;
  border-radius: 23px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-pickup-cancel {
  background: #f5f7fa;
  color: #606266;
}

.btn-pickup-cancel:active {
  transform: scale(0.95);
}

.pickup-confirm-btn {
  background: linear-gradient(135deg, #f5222d 0%, #ff7875 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(245, 34, 45, 0.35);
}

.pickup-confirm-btn:active {
  transform: scale(0.95);
  box-shadow: 0 2px 6px rgba(245, 34, 45, 0.25);
}

.pickup-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ===== 二次确认弹窗样式 ===== */
.confirm-popup-content {
  text-align: center;
  padding: 10px 0 20px;
}

.popup-icon-warning {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
}

.popup-icon-warning i {
  font-size: 28px;
  color: #fa8c16;
}

.popup-title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 12px;
  line-height: 1.4;
}

.popup-desc {
  font-size: 14px;
  color: #606266;
  margin: 0 0 12px;
  line-height: 1.6;
}

.popup-warning {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #fef0f0;
  border-radius: 8px;
  color: #f56c6c;
  font-size: 12px;
  margin: 0;
}

.popup-warning i {
  font-size: 14px;
}

.popup-footer {
  display: flex;
  gap: 12px;
  padding-top: 16px;
}

.btn-popup-cancel, .btn-popup-confirm {
  flex: 1;
  height: 44px;
  border-radius: 22px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-popup-cancel {
  background: #f5f7fa;
  color: #606266;
}

.btn-popup-cancel:active {
  transform: scale(0.95);
}

.btn-popup-confirm {
  background: linear-gradient(135deg, #f5222d 0%, #ff7875 100%);
  color: #fff;
}

.btn-popup-confirm:active {
  transform: scale(0.95);
}

.btn-popup-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
